import { FlipHorizontal2, FlipVertical2, X } from "lucide-react";
import { useState } from "react";
import Moveable from "react-moveable";
import { useRef } from "react";
import Image from "@/components/ui/image";
import { useMediaQuery } from "@mantine/hooks";
import { HoverBtn } from "@/components/styles";
import { lightenColor } from "@/utils/colorUtils";
import useUserStore, { Asset } from "@/state/user/useUserStore";

export const MoveableItem: React.FC<{
  asset: Asset;
  remove: any;
  hideControls?: boolean;
  color: string;
}> = ({ asset, remove, hideControls, color }) => {
  const [updateAsset] = useUserStore((state) => [state.updateAsset]);
  const [isHovered, setIsHovered] = useState(false);
  const [frame, setFrame] = useState({
    translate: [0, 0],
    scale: [1, 1],
    width: 150,
    height: 150,
    rotate: 0,
  });

  const targetRef = useRef<HTMLDivElement | null>(null);
  const moveableRef = useRef<Moveable | null>(null);

  const [bounds, setBounds] = useState({
    left: 10,
    top: 10,
    right: 10,
    bottom: 10,
  });

  const lighterColor = lightenColor(color, 10);

  const isMobile = useMediaQuery("(max-width: 768px)");

  return (
    <>
      <div
        style={{
          width: "150px",
          height: "150px",
          position: "absolute",
          right: "0",
          top: "0",
          zIndex: asset.zIndex,
          pointerEvents: hideControls ? "none" : "auto",
        }}
        onMouseEnter={() => !isMobile && !hideControls && setIsHovered(true)}
        onMouseLeave={() => !isMobile && !hideControls && setIsHovered(false)}
        ref={targetRef}
      >
        {isHovered && !hideControls && (
          <>
            <div
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                zIndex: 100,
              }}
            >
              <HoverBtn
                color={color}
                hoverColor={lighterColor}
                onClick={remove}
              >
                <X style={{ width: "12px", height: "12px" }} />
              </HoverBtn>
            </div>
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                zIndex: 99,
                flexDirection: "row",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "2px",
              }}
            >
              <HoverBtn
                onClick={() =>
                  updateAsset(asset.id, {
                    flipVertical: !asset.flipVertical,
                  })
                }
              >
                <FlipVertical2 style={{ width: "12px", height: "12px" }} />
              </HoverBtn>
              <HoverBtn
                onClick={() =>
                  updateAsset(asset.id, {
                    flipHorizontal: !asset.flipHorizontal,
                  })
                }
              >
                <FlipHorizontal2 style={{ width: "12px", height: "12px" }} />
              </HoverBtn>
            </div>
          </>
        )}
        <Image
          draggable={false}
          src={asset.img}
          style={{
            objectFit: "contain",
            transform: `scaleX(${asset.flipHorizontal ? -1 : 1}) scaleY(${
              asset.flipVertical ? -1 : 1
            })`,
          }}
          alt=""
          sizes="50vw"
          fill
        />
      </div>
      <Moveable
        ref={moveableRef}
        target={hideControls ? null : targetRef.current}
        draggable={true}
        resizable={true}
        scalable={true}
        rotatable={true}
        onDrag={({ beforeTranslate }) => {
          frame.translate = beforeTranslate;
        }}
        onResize={({ width, height }) => {
          frame.width = width;
          frame.height = height;
        }}
        onScale={({ scale }) => {
          frame.scale = scale;
        }}
        bounds={bounds}
        onRotate={({ beforeRotate }) => {
          frame.rotate = beforeRotate;
        }}
        onRender={({ target }) => {
          target.style.transform = `translate(${frame.translate[0]}px, ${frame.translate[1]}px) rotate(${frame.rotate}deg)`;
          target.style.width = `${frame.width}px`;
          target.style.height = `${frame.height}px`;
        }}
      />
    </>
  );
};
